import type { NextApiRequest, NextApiResponse } from 'next'
import {
  type Language,
  type OldProductDTO,
  type PostNordCountry,
  type PostNordDeliveryOptionsRequest,
  type PostNordDeliveryOptionsRequestFE,
  type PostNordDeliveryOptionsResponseFE,
  type ShippingProviderConfig
} from 'ecosystem'
import { postnordApi, useLocaleCredentials as setLocaleCredentials } from 'api'
import storefrontApi from 'api/storefront-api'
import { calculateOrderTotalWeight, roundNumber } from 'shared-utils'
import { handlerWithLogging } from '../../../../logging'
import { generatePostnordDeliveryOptions } from './utils/generatePostnordDeliveryOptions'
import { generatePostnordDeliveryTypes } from './utils/generatePostnordDeliveryTypes'

interface PostnordGetDeliveryOptionsHandlerOpts {
  shippingProviderConfig?: ShippingProviderConfig
  locale?: Language
  customerId?: string | null
}

export const postnordGetDeliveryOptionsHandler = handlerWithLogging(
  async (
    req: NextApiRequest,
    res: NextApiResponse<PostNordDeliveryOptionsResponseFE | { error: string }>,
    opts: PostnordGetDeliveryOptionsHandlerOpts
  ) => {
    const localeForCredentials = (req.cookies.NEXT_LOCALE as Language) || 'sv'
    const locale = opts.locale || localeForCredentials

    setLocaleCredentials(localeForCredentials)

    if (req.method !== 'POST') {
      res.status(405).json({ error: 'Method not supported.' })
      return
    }

    const {
      cartTotalAmount = 0,
      orderItems,
      ...body
    } = req.body as PostNordDeliveryOptionsRequestFE

    let responseError: string | null

    try {
      if (!opts.shippingProviderConfig?.postnordOptions?.customerKey) {
        throw new Error('PostNord customer key is required')
      }

      const fullProducts: OldProductDTO[] = await (async () => {
        const productIds = orderItems?.map((item) => item.productId) ?? []

        if (!productIds?.length) {
          return []
        }

        const productsResponse = await storefrontApi.products.listProductsFull(
          {
            productIds,
            ...(opts.customerId ? { customerId: opts.customerId } : {})
          },
          {
            size: productIds.length
          }
        )

        return productsResponse.content
      })()

      // Postnord requires weight > 0
      const { totalWeight } = calculateOrderTotalWeight(
        orderItems?.map(({ quantity, productId }) => ({ quantity, product: { id: productId } })) ??
          [],
        fullProducts
      )
      const parcelTotalWeight = roundNumber(totalWeight, 3) || 1

      const allowedDeliveryTypes = opts.shippingProviderConfig.postnordOptions
        .conditionalDeliveryTypes
        ? generatePostnordDeliveryTypes(
            opts.shippingProviderConfig.postnordOptions.conditionalDeliveryTypes,
            cartTotalAmount,
            body.recipient.address.countryCode as PostNordCountry,
            parcelTotalWeight
          )
        : []

      const payload: PostNordDeliveryOptionsRequest = {
        customer: {
          customerKey: opts.shippingProviderConfig.postnordOptions?.customerKey
        },
        warehouses: opts.shippingProviderConfig.postnordOptions?.warehouses ?? [],
        ...(allowedDeliveryTypes.length
          ? {
              filter: {
                deliveryTypes: allowedDeliveryTypes
              }
            }
          : {}),
        ...body
      }

      const { data, error } = await postnordApi.getDeliveryOptions(payload, locale)

      if (data) {
        const newDataResponse = generatePostnordDeliveryOptions({
          data,
          recipientCountryCode: body.recipient.address.countryCode as PostNordCountry,
          shippingProviderConfig: opts.shippingProviderConfig,
          cartTotalAmount,
          allowedDeliveryTypes
        })

        res.status(200).json(newDataResponse)
        return
      }

      responseError = error ?? 'Data is not found'
    } catch (e) {
      responseError = e instanceof Error ? e.message : 'unknown'
    }

    res.status(400).json({ error: responseError })
  }
)
